import { useState, useContext, useCallback } from "react";
import dayjs from "dayjs";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import { TailSpin } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import {
  useGetOpeningHoursDetails,
  useUpdateRestaurant,
} from "src/data/restaurant/queries/restaurant";
import {
  useAddRestaurantLeavesBulk,
  useDeleteRestaurantLeavesBulk,
} from "src/data/restaurant/queries/leaves";
import { DayHoursList, HourType } from "src/business/restaurant/restaurant";
import { RestaurantLeave } from "src/business/restaurant/leave";

import { NoAccess, SnackBar, TipsCard } from "src/design";
import { colorBackground } from "src/design/tokens";
import { mapHoursToScheduleEntries } from "src/data/restaurant/mappers/restaurantMapper";
import { SchedulePageHeader } from "./components/SchedulePageHeader";
import { WeeklyScheduleSection } from "./components/WeeklyScheduleSection";
import { LeaveDaysSection } from "./components/LeaveDaysSection";
import { BookingDurationSection } from "./components/BookingDurationSection";
import { LeaveToggleEvent } from "./components/LeavesModal";

export const OpeningHours = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { restaurants } = useContext(RestaurantContext);
  const { user } = useContext(UserContext);

  const {
    loading: detailsLoading,
    restaurantDetails,
    error: detailsError,
    getRestaurantDetails,
  } = useGetOpeningHoursDetails(restaurantId);

  const restaurant = restaurants?.find((r) => r.id === restaurantId) || null;

  const canEditSchedule = isFeatureSectionEnabled(
    ["opening_hours_update"],
    "opening_hours_management",
    user,
    restaurant?.capabilities.map((c) => c.name),
  );

  // ── editing state ──────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // ── schedule ───────────────────────────────────────────────────────────────
  const [hours, setHours] = useState<DayHoursList>([]);
  const [scheduleChanged, setScheduleChanged] = useState(false);

  // ── booking duration ───────────────────────────────────────────────────────
  /** null means the user hasn't changed it this edit session */
  const [pendingBookingDuration, setPendingBookingDuration] = useState<number | null>(null);

  // ── leaves ─────────────────────────────────────────────────────────────────
  const [pendingLeaveAdds, setPendingLeaveAdds] = useState<string[]>([]);
  const [pendingLeaveDeletes, setPendingLeaveDeletes] = useState<RestaurantLeave[]>([]);
  /** holds the last saved leaves list until the refetch resolves */
  const [lastSavedLeaves, setLastSavedLeaves] = useState<string[] | null>(null);

  // ── mutations ──────────────────────────────────────────────────────────────
  const { updateRestaurant } = useUpdateRestaurant(restaurant?.id ?? "");
  const { mutateAsync: addLeavesBulk } = useAddRestaurantLeavesBulk(restaurant?.id ?? "");
  const { mutateAsync: deleteLeavesBulk } = useDeleteRestaurantLeavesBulk(restaurant?.id ?? "");

  // ── derived ────────────────────────────────────────────────────────────────
  const hasAnyChange =
    scheduleChanged ||
    pendingBookingDuration !== null ||
    pendingLeaveAdds.length > 0 ||
    pendingLeaveDeletes.length > 0;

  /** Leaves to display during editing: saved leaves minus pending deletes, plus pending adds.
   *  restaurantDetails.leaves are raw ISO timestamps from the API, so normalise to YYYY-MM-DD
   *  before comparing against pendingLeaveDeletes.date which is already in that format. */
  const displayLeaves =
    isEditing && restaurantDetails
      ? [
          ...restaurantDetails.leaves
            .map((d) => dayjs(d).format("YYYY-MM-DD"))
            .filter((d) => !pendingLeaveDeletes.some((l) => l.date === d)),
          ...pendingLeaveAdds,
        ]
      : (lastSavedLeaves ?? (restaurantDetails?.leaves ?? []).map((d) => dayjs(d).format("YYYY-MM-DD")));

  const pendingDeleteDates = pendingLeaveDeletes.map((l) => l.date);

  // ── handlers ───────────────────────────────────────────────────────────────
  const startEditing = () => {
    if (!restaurantDetails) return;
    setHours(
      restaurantDetails.schedule.map((h) => ({
        ...h,
        morning: { ...h.morning },
        evening: h.evening ? { ...h.evening } : null,
      })),
    );
    setScheduleChanged(false);
    setPendingBookingDuration(null);
    setPendingLeaveAdds([]);
    setPendingLeaveDeletes([]);
    setIsEditing(true);
  };

  const resetPendingState = () => {
    setScheduleChanged(false);
    setPendingBookingDuration(null);
    setPendingLeaveAdds([]);
    setPendingLeaveDeletes([]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    resetPendingState();
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(false);

    try {
      const promises: Promise<unknown>[] = [];

      // Only include restaurant fields that actually changed
      const restaurantUpdate: { schedule?: ReturnType<typeof mapHoursToScheduleEntries>; bookingDuration?: number } = {};
      if (scheduleChanged) {
        restaurantUpdate.schedule = mapHoursToScheduleEntries(hours);
      }
      if (pendingBookingDuration !== null) {
        restaurantUpdate.bookingDuration = pendingBookingDuration;
      }
      if (Object.keys(restaurantUpdate).length > 0) {
        promises.push(updateRestaurant(restaurantUpdate));
      }

      if (pendingLeaveAdds.length > 0) {
        promises.push(addLeavesBulk(pendingLeaveAdds));
      }
      if (pendingLeaveDeletes.length > 0) {
        promises.push(deleteLeavesBulk(pendingLeaveDeletes.map((l) => l.id)));
      }

      await Promise.all(promises);

      // Leaves are still mirrored locally: the bulk add/delete endpoints are
      // separate mutations that do not invalidate this query, so the list on
      // screen would otherwise revert until the next read.
      if (pendingLeaveAdds.length > 0 || pendingLeaveDeletes.length > 0) {
        setLastSavedLeaves(displayLeaves);
      }

      setSaveSuccess(true);
      setIsEditing(false);
      // Hold the pending values until the re-read lands. Clearing them first
      // would drop the form back to the pre-save duration for one round trip.
      await getRestaurantDetails();
      resetPendingState();
    } catch (err) {
      console.error(err);
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDayChange = (index: number, updated: HourType) => {
    setHours((prev) => {
      const newHours = [...prev];
      newHours[index] = updated;
      return newHours;
    });
    if (!scheduleChanged) setScheduleChanged(true);
  };

  const handleBookingDurationChange = useCallback((value: number) => {
    setPendingBookingDuration(value);
  }, []);

  const handleLeaveToggle = useCallback(
    ({ date, leave, wasSelected }: LeaveToggleEvent) => {
      if (wasSelected) {
        if (leave !== null) {
          // Persisted leave → mark for deletion
          setPendingLeaveDeletes((prev) => [...prev, leave]);
        } else {
          // Was a pending add → undo it
          setPendingLeaveAdds((prev) => prev.filter((d) => d !== date));
        }
      } else {
        const isPendingDelete = pendingLeaveDeletes.some((l) => l.date === date);
        if (isPendingDelete) {
          // Was pending deletion → undo the deletion
          setPendingLeaveDeletes((prev) => prev.filter((l) => l.date !== date));
        } else {
          // New date → add to pending adds
          setPendingLeaveAdds((prev) => [...prev, date]);
        }
      }
    },
    [pendingLeaveDeletes],
  );

  // ── guards ─────────────────────────────────────────────────────────────────
  if (!restaurantId) {
    navigate(
      restaurants[0]
        ? `/restaurant/${restaurants[0].id}`
        : "/restaurant/no-restaurant",
      { replace: true },
    );
    return null;
  }

  if (!restaurant || detailsLoading) {
    return (
      <Box sx={{ display: "flex", width: "100%", justifyContent: "center", mt: 3 }}>
        <TailSpin color="#00BFFF" height={80} width={80} />
      </Box>
    );
  }

  // The read is gated on opening_hours_read — the same permission this page's
  // edits need — so a refusal here means the user is not scoped to the opening
  // hours at all: say so rather than render an empty page.
  if (detailsError) return <NoAccess />;

  if (!restaurantDetails) return null;

  const schedule = isEditing ? hours : restaurantDetails.schedule;

  return (
    <Grid container justifyContent="center">
      <Grid
        item
        xs={12}
        style={{ maxWidth: "72rem" }}
        sx={{ width: "100%", px: { xs: 2, md: 4 }, py: 3 }}
      >
        <Box sx={{ backgroundColor: colorBackground }}>
          <SchedulePageHeader
            isEditing={isEditing}
            updating={isSaving}
            hasChanged={hasAnyChange}
            canEdit={canEditSchedule}
            onEdit={startEditing}
            onCancel={handleCancel}
            onSave={handleSave}
          />

          <Grid container spacing={2} direction="column">
            <LeaveDaysSection
              leaves={displayLeaves}
              editing={isEditing}
              restaurantId={restaurant.id}
              pendingAdds={pendingLeaveAdds}
              pendingDeleteDates={pendingDeleteDates}
              onToggle={handleLeaveToggle}
            />

            <BookingDurationSection
              bookingDuration={
                pendingBookingDuration ?? restaurantDetails.bookingDuration
              }
              disabled={!isEditing}
              onChange={handleBookingDurationChange}
            />

            <WeeklyScheduleSection
              schedule={schedule}
              editing={isEditing}
              onDayChange={handleDayChange}
            />

            <Grid item>
              <TipsCard
                title={t("schedule.tips.title")}
                tips={[
                  t("schedule.tips.holidays"),
                  t("schedule.tips.closures"),
                  t("schedule.tips.bookingDuration"),
                ]}
              />
            </Grid>
          </Grid>
        </Box>

        <SnackBar
          onClose={() => setSaveSuccess(false)}
          open={saveSuccess}
          message={t("schedule.saveSuccess")}
          severity="success"
        />
        <SnackBar
          onClose={() => setSaveError(false)}
          open={saveError}
          message={t("schedule.saveError")}
          severity="error"
        />
      </Grid>
    </Grid>
  );
};
